import { useState, useEffect } from "react";
import { bindActionCreators, Dispatch } from 'redux';
import { connect, RootStateOrAny } from 'react-redux';
import { useLocation } from 'react-router';
import moment from 'moment';

import { Typography, Alert, Tabs, Button, Skeleton, Table} from "antd";
import { MedicineBoxOutlined, PrinterOutlined } from "@ant-design/icons";

import { formatDate, formatPhoneNumber, formatZipCode, getDisplayCompanyId } from 'utils/helpers';
import { ErrorResponse } from "utils/types";
import notifier from 'utils/notifier';
import { UserRole } from "utils/enums";

import { downloadMemberDocument, getMemberDocuments, getCategory, updateCategory, deleteMemberDoc } from 'store/eligibility/documents/actions';
import { getCMWorkList, getProvider, getMemberConditionCodes, validateMeasures } from 'store/eligibility/actions';
import { saveTask, deleteTask } from 'store/eligibility/mcp/actions';
import { getEddDate, saveEddDate } from 'store/eligibility/edd/actions';
import { saveLmpDate, getLmpDate } from 'store/eligibility/lmp/actions';
import { saveHealth } from 'store/eligibility/health/actions';
import { postAuthHistory } from 'store/authorization/history/actions';
import { getPlans, savePlan, getNotes, saveNote, getDetailPlan, getMedDetail, getNoteLogs, getPlanLogs } from 'store/eligibility/pac/actions';
import { get, deleteNote } from 'store/eligibility/noteDocs/actions';
import { getPrintFile } from 'store/eligibility/measures/actions';
import { updateStatusAndComment, deleteHccDoc } from 'store/eligibility/hcc/actions';
import { getDiags, getDetails } from 'store/eligibility/hcc/actions';
import { getCopayInfo } from 'store/eligibility/copay/actions';
import { uploadFilestoAuth } from 'store/authorization/files/actions';
import { getPortalAuth } from 'store/authorization/portal/actions';
import { getAttachInfo } from 'store/authorization/attach/actions';
import { getAttachments } from 'store/claim/member/actions';
import { postHistory, getEZ } from 'store/claim/actions';
import { getForMember as getMemberMeasures, getHedisMeasures } from 'store/eligibility/measures/actions';
import { get as getMemberImmunization } from 'store/eligibility/immunization/actions';
import { get as getEncounter, save as saveEncounter } from 'store/eligibility/encounter/actions';
import { saveMemberMeasure, getEmCodes } from 'store/measure/actions';
import { getAuthModifierCode } from 'store/authorization/modifierCode/actions';
import { submitMemberMeasures, saveHedisClaim } from 'store/eligibility/claimGenerator/actions';

import MemberDocumentsTab from "components/Eligibility/MemberEligibilityDetailTables/MemberDocumentsTab";
import PostAcuteCareClinicalPlanTab from "components/Eligibility/MemberEligibilityDetailTables/PostAcuteCareClinicalPlanTab";
import PostAcuteCareNotesTab from "components/Eligibility/MemberEligibilityDetailTables/PostAcuteCareNotesTab";
import HealthAssessmentTab from "components/Eligibility/MemberEligibilityDetailTables/HealthAssessmentTab";
import CaseManagementWorkListTab from "components/Eligibility/MemberEligibilityDetailTables/CaseManagementWorkListTab";
import ClaimHistoryModal from "components/Eligibility/MemberEligibilityDetailTables/ClaimHistoryModal";
import PcpDetailModal from "components/Eligibility/MemberEligibilityDetailTables/PcpDetailModal";
import AuthorizationHistoryModal from "components/Eligibility/MemberEligibilityDetailTables/AuthorizationHistoryModal/";
import ClaimGenerator from "components/Eligibility/ClaimGenerator";

import MemberEligibilityInfoDetailPageProps from './Proptypes';

import './index.scss';

const { Title } = Typography;
const { TabPane } = Tabs;


const MemberEligibilityInfoDetailPage = (props: MemberEligibilityInfoDetailPageProps) => {
  const location: any = useLocation();
  const eligibilityMemberInfo = location?.state.eligibilityMemberInfo;
  const [isPcpDetailModalVisible, setIsPcpDetailModalVisible] = useState(false);
  const [isAuthHistoryModalVisible, setIsAuthHistoryModalVisible] = useState(false);
  const [isClaimHistoryModalVisible, setIsClaimHistoryModalVisible] = useState(false);
  const [copayInfo, setCoPayInfo] = useState('');
  const [primaryCareProvider, setPrimaryCareProvider] = useState<any>({}); //object way too long to define
  const [activeKey, setActiveKey] = useState('1');
  const [hideMeasures, setHideMeasures] = useState(true);
  const [conditionCodeDataSource, setConditionCodeDataSource] = useState<any[]>([]);
  const [measureYear, setMeasureYear] = useState<number>(moment().year());

  useEffect(() => {

    //this is used to keep track of previous path to retain the search table results when going back to the /eligibility/search table
    sessionStorage.setItem('previous_location', '/eligibility/member-detail');

    getMemberConditionCodes();
    onPageLoadCurrentTab();
    getCoPayInfo();
    getProvider();
  }, []);

  const conditionCodeColumns = [
    {
      title: "Code",
      key: "RmiCondition",
      dataIndex: "RmiCondition",
    },
    {
      title: "Sub Code",
      key: "RmiSubCode",
      dataIndex: "RmiSubCode",
    },
    {
      title: "Description",
      key: "RmiDescription",
      dataIndex: "RmiDescription",
    },
    {
      title: "From",
      key: "RmiFromDate",
      dataIndex: "RmiFromDate",
      render: (_: string, record: any, __: number) => formatDate(record.RmiFromDate, 'MM/DD/YYYY')
    },
    {
      title: "To",
      key: "RmiToDate",
      dataIndex: "RmiToDate",
      render: (_: string, record: any, __: number) => formatDate(record.RmiToDate, 'MM/DD/YYYY')
    },
  ];

  const switchTabs = (activeKey: string) => {
    setActiveKey(activeKey);
  }

  const isAuthHistoryModalVisibleHandler = () => {
    setIsAuthHistoryModalVisible(false);
  };

  const showAuthHistoryModal = () => {
    setIsAuthHistoryModalVisible(true);
  };

  const showPcpDetailModal = () => {
    setIsPcpDetailModalVisible(true);
  }

  const showClaimHistoryModal = () => {
    setIsClaimHistoryModalVisible(true);
  };

  const claimHistoryModalOkHandler = () => {
    setIsClaimHistoryModalVisible(false);
  };

  const claimHistoryCancelHandler = () => {
    setIsClaimHistoryModalVisible(false);
  };

  const pcpDetailModalCloseHandler = () => {
    setIsPcpDetailModalVisible(false);
  }

  const getProvider = () => {
    return props.getProvider(eligibilityMemberInfo.RmiProvKeyId, eligibilityMemberInfo.RmiCompanyId)
      .then((provider: Object) => {
        setPrimaryCareProvider(provider);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const getCoPayInfo = () => {
    return props.getCopayInfo(eligibilityMemberInfo.RmiCompanyId, eligibilityMemberInfo.RmiHpCode, eligibilityMemberInfo.RmiOpt)
      .then((response: string) => {
        setCoPayInfo(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const onPageLoadCurrentTab = () => {
    const hideMeasures = eligibilityMemberInfo.RmiHpCode === "HNCC" || eligibilityMemberInfo.RmiHpCode === "BSPP";
    setHideMeasures(hideMeasures);

    //if both Heath Assessment and Medicare HCC tabs are hidden, then Member Document tab is active by default
    if (hideMeasures && eligibilityMemberInfo.RmiMedicareFlag !== 'Y') setActiveKey('3');

    //if Health Assessment tab is hidden only, then set Medicare HCC tab is active by default
    else if (hideMeasures && eligibilityMemberInfo.RmiMedicareFlag === 'Y') setActiveKey('2');

    //if Medicare HCC tab is hidden only, then set Health Assessment tab is active by default
    else if (!hideMeasures && eligibilityMemberInfo.RmiMedicareFlag !== 'Y') setActiveKey('1');
  }

  //Legacy code has the age displayed in months
  const calcAgeInYrsMo = (birthday: string) => {
    if (birthday) {
      const years = moment().diff(birthday, 'years');
      const totalMonths = moment().diff(birthday, 'months');
      const months = totalMonths % 12;

      return years + ' years ' + months + (months > 0 ? ' months' : ' month');
    }
    return null;
  }

  const getMemberConditionCodes = () => {
    return props.getMemberConditionCodes(eligibilityMemberInfo.RmiCompanyId, eligibilityMemberInfo.RmiMembKeyId, sessionStorage.user_id)
      .then((response: any[]) => {
        setConditionCodeDataSource(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  /**
   * hide - print tab section, patient info
   * show - member info and condition code table
   */
  const print = () => {
    $(".print-eligibility-member-info, .print-member-info, .condition-code-table, .title, .print-logo").removeClass("exclude-print").addClass("include-print");
    $(".print-hcc-section, .print-tab-section, .patient-info").removeClass('include-print').addClass("exclude-print");
    window.print();
  }

  return (
    <div className="print-member-info row flex-lg-wrap justify-content-start mb-3 ml-0 mt-3 pt-2">
      <hr />
      <div className="col-lg-24 col-md-24 patient-info">
        <span className="mr-2"><b>Patient Name:</b> {eligibilityMemberInfo.RmiRevFullName}</span> <span className="mr-2"><b>DOB:</b> {formatDate(eligibilityMemberInfo.RmiBirth, 'MM/DD/YYYY')}</span> <span><b>Gender:</b> {eligibilityMemberInfo.RmiSex}</span>
      </div>
      <div className="header-title">
        <Title level={4} className="ml-3">Member Eligibility and Assessment</Title>
      </div>
      <Title className="title" level={3}>Eligibility</Title>

      <hr />
      <div className="col">

        <Button className="mr-2 green-button" onClick={showAuthHistoryModal} icon={ <MedicineBoxOutlined />}>
          Authorization History
        </Button>

        <Button className="green-button" onClick={showClaimHistoryModal} icon={ <MedicineBoxOutlined />}>
          Claim History
        </Button>

        <Button type="primary" className="print-button" style={{float: 'right'}} onClick={print} icon={<PrinterOutlined />}>
          Print
        </Button>

        <AuthorizationHistoryModal
          getPortalAuth={props.getPortalAuth}
          postAuthHistory={props.postAuthHistory}
          isAuthHistoryModalVisibleHandler={isAuthHistoryModalVisibleHandler}
          eligibilityMemberInfo={eligibilityMemberInfo}
          isAuthHistoryModalVisible={isAuthHistoryModalVisible}
          getAttachInfo={props.getAttachInfo}
          isPosting={props.isPosting}
          isFetchingAuth={props.isFetchingAuth}
        />

        <ClaimHistoryModal
          postHistory={props.postHistory}
          getEZ={props.getEZ}
          getAttachments={props.getAttachments}
          eligibilityMemberInfo={eligibilityMemberInfo}
          isClaimHistoryModalVisible={isClaimHistoryModalVisible}
          claimHistoryModalOkHandler={claimHistoryModalOkHandler}
          claimHistoryCancelHandler={claimHistoryCancelHandler}
          isPostingHistory={props.isPostingHistory}
          isFetchingEZ={props.isFetchingEZ}
        />

        <PcpDetailModal
          primaryCareProvider={primaryCareProvider}
          isPcpDetailModalVisible={isPcpDetailModalVisible}
          pcpDetailModalCloseHandler={pcpDetailModalCloseHandler}
        />
      </div>

      {props.isFetchingProvider ? (
        <>
          <Skeleton
            active
            paragraph={{ rows: 3 }}
          />
          <br />
          <Skeleton
            avatar
            active
            paragraph={{ rows: 3 }}
          />
          <br />
          <Skeleton
            active
            paragraph={{ rows: 3 }}
          />
        </>
      ) : (
        <div className="print-eligibility-member-info print-padding member-search row flex-lg-wrap justify-content-start mb-4 ml-0 pt-4 pb-4">
          <div className="col-lg-8 col-md-8">
            <Title level={5}>Member Information</Title>
            <table className="test" style={{ textAlign: "left" }}>
              <tbody>
                <tr>
                  <th className="bold">Member ID:</th>
                  <td>{eligibilityMemberInfo.RmiMembId}</td>
                </tr>
                <tr>
                  <th className="bold">Member Name:</th>
                  <td>{eligibilityMemberInfo.RmiRevFullName}</td>
                </tr>
                <tr>
                  <th className="bold">Date of Birth:</th>
                  <td>{formatDate(eligibilityMemberInfo.RmiBirth, 'MM/DD/YYYY')}</td>
                </tr>
                <tr>
                  <th className="bold">Gender / Age:</th>
                  <td>{eligibilityMemberInfo.RmiSex === 'M' ? 'Male' : 'Female'}
                    &nbsp; / &nbsp;
                    {calcAgeInYrsMo(eligibilityMemberInfo.RmiBirth)}
                  </td>
                </tr>
                <tr>
                  <th className="bold">Address:</th>
                  <td>
                    {eligibilityMemberInfo.RmiStreet} {eligibilityMemberInfo.RmiStreet2}
                  </td>
                </tr>
                <tr>
                  <th className="bold">City, State, Zip:</th>
                  <td>
                    {eligibilityMemberInfo.RmiCity}, &nbsp;
                    {eligibilityMemberInfo.RmiState} &nbsp;
                    {formatZipCode(eligibilityMemberInfo.RmiZip)} &nbsp;
                  </td>
                </tr>
                <tr>
                  <th className="bold">Phone:</th>
                  <td>
                    {formatPhoneNumber(eligibilityMemberInfo.RmiPhone)}
                  </td>
                </tr>
                <tr>
                  <th className="bold">Language:</th>
                  <td>
                    {eligibilityMemberInfo.RmiLanguage}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="col-lg-8 col-md-8 mt-print-1">
            <Title level={5}>Member Benefit Information</Title>
            <table className="test" style={{ textAlign: "left" }}>
              <tbody>
                <tr>
                  <th className="bold">IPA:</th>
                  <td>{getDisplayCompanyId(eligibilityMemberInfo.RmiCompanyId)}</td>
                </tr>
                <tr>
                  <th className="bold">Health Plan:</th>
                  <td>{eligibilityMemberInfo.RmiHpName}</td>
                </tr>
                <tr>
                  <th className="bold">Benefit Option:</th>
                  <td>{eligibilityMemberInfo.RmiOpt}</td>
                </tr>
                <tr>
                  <th className="bold">PCP OV Co-pay:</th>
                  <td>{copayInfo}</td>
                </tr>
                <tr>
                  <th className="bold">Benefits Effective:</th>
                  <td>{formatDate(eligibilityMemberInfo.RmiStartDate, 'MM/DD/YYYY')}</td>
                </tr>
                <tr>
                  <th className="bold">Benefits Termed:</th>
                  <td>
                    {
                      formatDate(eligibilityMemberInfo.RmiEndDate, 'MM/DD/YYYY') ?
                        formatDate(eligibilityMemberInfo.RmiEndDate, 'MM/DD/YYYY') + '*' :
                        ''
                    }
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="col-lg-8 col-md-8 mt-print-1">
            <Title level={5}>Primary Care Provider Information</Title>
            <table className="test" style={{ textAlign: "left" }}>
              <tbody>
                <tr>
                  <th className="bold">PCP Name:</th>
                  <td><Button className="p-0 pcp-link" type="link" onClick={showPcpDetailModal}>{primaryCareProvider.RmiRevFullName}</Button></td>
                </tr>
                <tr>
                  <th className="bold">Provider ID:</th>
                  <td>{primaryCareProvider.RmiProvId}</td>
                </tr>
                <tr>
                  <th className="bold">Specialty:</th>
                  <td>{primaryCareProvider.RmiProvSpecialties}</td>
                </tr>
                <tr>
                  <th className="bold">Phone Number:</th>
                  <td>{formatPhoneNumber(primaryCareProvider.RmiPhone)}</td>
                </tr>
                <tr>
                  <th className="bold">Fax Number:</th>
                  <td>{formatPhoneNumber(primaryCareProvider.RmiFax)}</td>
                </tr>
                <tr>
                  <th className="bold">Termination:</th>
                  <td>{primaryCareProvider.RmiTermDate}</td>
                </tr>
              </tbody>
            </table>
          </div>

          { conditionCodeDataSource?.length > 0 &&
            <div className="condition-code-table mt-3 ml-3 mr-3">
              <Table
                columns={conditionCodeColumns}
                dataSource={conditionCodeDataSource}
                pagination={false}
              />
            </div>
          }

          <div className="member-search mt-3 ml-3 mr-3">
            <Alert
              message=""
              description={
                <>
                  ELIGIBILITY PROVIDED BY THE IPA DOES NOT GUARANTEE THE MEMBER&#8217;S UP-TO-DATE ELIGIBILITY INFORMATION.
                  PLEASE VERIFY ELIGIBILITY AT THE TIME OF SERVICE WITH THE MEMBER&#8217;S HEALTH PLAN.
                  ADV IS NOT RESPONSIBLE FOR SERVICES RENDERED FOR MEMBERS NOT ELIGIBLE WITH THE IPA AT THE TIME OF SERVICE.
                </>
              }
            />
          </div>
        </div>
      )}

      <Tabs
        type="card"
        className="print-tab-section member-search tabs row flex-lg-wrap justify-content-start ml-0"
        activeKey={activeKey}
        onChange={(onChange) => switchTabs(onChange)}
      >
        { !hideMeasures &&
          <TabPane tab="Health Assessment" key="1">
            <HealthAssessmentTab
              saveHealth={props.saveHealth}
              eligibilityMemberInfo={eligibilityMemberInfo}
              primaryCareProvider={primaryCareProvider}
              getEddDate={props.getEddDate}
              saveEddDate={props.saveEddDate}
              getLmpDate={props.getLmpDate}
              saveLmpDate={props.saveLmpDate}
              getPrintFile={props.getPrintFile}
              isFetchingPrintFile={props.isFetchingPrintFile}
              activeTab={activeKey}
              updateStatusAndComment={props.updateStatusAndComment}
              getDetails={props.getDetails}
              getDiags={props.getDiags}
              isFetchingDiags={props.isFetchingDiags}
              isFetchingDetails={props.isFetchingDetails}
              measureYear={measureYear}
              setMeasureYear={setMeasureYear}
            />
          </TabPane>
        }
        <TabPane tab="Member Documents" key="3">
          <MemberDocumentsTab
            eligibilityMemberInfo={eligibilityMemberInfo}
            primaryCareProviderData={primaryCareProvider}
            getMemberDocuments={props.getMemberDocuments}
            downloadMemberDocument={props.downloadMemberDocument}
            getCategory={props.getCategory}
            updateCategory={props.updateCategory}
            uploadFilestoAuth={props.uploadFilestoAuth}
            deleteMemberDoc={props.deleteMemberDoc}
            isUploading={props.isUploading}
            isFetching={props.isFetching}
            activeTab={activeKey}
          />
        </TabPane>
        
      // WorkItem - 35987: Removed tabs for Post Acute Care Clinical Plan + Post Acute Care Notes
        
        { sessionStorage.user_role === UserRole.CASE &&
          <TabPane tab="Case Management Worklist" key="6">
            <CaseManagementWorkListTab
              getCMWorkList={props.getCMWorkList}
              saveTask={props.saveTask}
              deleteTask={props.deleteTask}
              eligibilityMemberInfo={eligibilityMemberInfo}
              primaryCareProvider={primaryCareProvider}
              uploadFilestoAuth={props.uploadFilestoAuth}
              getCategory={props.getCategory}
              isFetchingCM={props.isFetchingCM}
              isUploading={props.isUploading}
              activeTab={activeKey}
            />
          </TabPane>
        }
      </Tabs>

      {!hideMeasures && activeKey === "1" && (
        <>
          <ClaimGenerator
            getMemberMeasures={props.getMemberMeasures}
            getHedisMeasures={props.getHedisMeasures}
            getMemberImmunization={props.getMemberImmunization}
            getEncounter={props.getEncounter}
            saveEncounter={props.saveEncounter}
            saveMemberMeasure={props.saveMemberMeasure}
            getEmCode={props.getEmCodes}
            getAuthModifierCode={props.getAuthModifierCode}
            validateMeasures={props.validateMeasures}
            submitMemberMeasures={props.submitMemberMeasures}
            saveHedisClaim={props.saveHedisClaim}
            eligibilityMemberInfo={eligibilityMemberInfo}
            primaryCareProvider={primaryCareProvider}
            getCategory={props.getCategory}
            uploadFilestoAuth={props.uploadFilestoAuth}
            isUploading={props.isUploading}
            isFetchingMember={props.isFetchingMember}
            isGetting={props.isGetting}
            isFetchingEmCodes={props.isFetchingEmCodes}
            measureYear={measureYear}
          />
        </>
      )}
    </div>
  );
};

const mapStateToProps = (store: RootStateOrAny) => ({
  isFetchingProvider: store.rootReducer.eligibility.isFetchingProvider, // Primary Care Provider Information
  isPosting: store.rootReducer.authHistory.isPosting, //Authorization History modal
  isPostingHistory: store.rootReducer.claim.isPostingHistory, //Claim History modal
  isFetchingNoteLogs: store.rootReducer.eligibilityPAC.isFetchingNoteLogs, //Note Logs modal on the detail page
  isFetchingDetails: store.rootReducer.eligibilityHCC.isFetchingDetails, //Diagnosis History modal
  isFetchingCM: store.rootReducer.eligibility.isFetchingCM, //Case management table
  isFetchingPlans: store.rootReducer.eligibilityPAC.isFetchingPlans, //post acute care plan table
  isFetchingDiags: store.rootReducer.eligibilityHCC.isFetchingDiags, //Medicare Hcc tables
  isFetchingPlanLogs: store.rootReducer.eligibilityPAC.isFetchingPlanLogs, //Clinical Plan Notes log table
  isFetching: store.rootReducer.eligibilityDocuments.isFetching, //fetching download documents
  isUploading: store.rootReducer.authFiles.isUploading, //Upload member document spinner
  isFetchingPrintFile: store.rootReducer.eligibilityMeasures.isFetchingPrintFile, //getting health assessment form from server
  isFetchingEZ: store.rootReducer.claim.isFetchingEZ, //fetching claim detail modal content
  isFetchingAuth: store.rootReducer.authPortal.isFetching, // fetch authorization detail modal content
  isFetchingMember: store.rootReducer.eligibilityMeasures.isFetchingMember, //fetch get member measures
  isGetting: store.rootReducer.eligibilityEncounter.isGetting, //getting encounter member vitals
  isFetchingEmCodes: store.rootReducer.measure.isFetchingEmCodes, //fetches emCode for autocomplete
  isSubmittingMeasure: store.rootReducer.eligibility.isSubmittingMeasure, //submitting measures to the final submission page from popup 2
  saveHedisClaim: store.rootReducer.eligibility.isSavingHedisClaim,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    getProvider,
    postAuthHistory,
    postHistory,
    saveHealth,
    saveEddDate,
    getEddDate,
    saveLmpDate,
    getLmpDate,
    getDiags,
    getCopayInfo,
    getMemberDocuments,
    downloadMemberDocument,
    getPlans,
    savePlan,
    getMemberConditionCodes,
    getNotes,
    saveNote,
    getPortalAuth,
    getEZ,
    getAttachments,
    getCMWorkList,
    saveTask,
    deleteTask,
    getCategory,
    getDetails,
    getDetailPlan,
    getMedDetail,
    getPlanLogs,
    get,
    getNoteLogs,
    getPrintFile,
    updateStatusAndComment,
    updateCategory,
    uploadFilestoAuth,
    deleteMemberDoc,
    deleteHccDoc,
    deleteNote,
    getAttachInfo,
    getMemberMeasures,
    getHedisMeasures,
    getMemberImmunization,
    getEncounter,
    saveEncounter,
    saveMemberMeasure,
    getEmCodes,
    getAuthModifierCode,
    validateMeasures,
    submitMemberMeasures,
    saveHedisClaim,
  },
  dispatch,
);

export default connect(mapStateToProps, mapDispatchToProps)(MemberEligibilityInfoDetailPage);